import React, { useEffect, useState } from 'react'
import Header from '../components/Header'
import { supabase } from '../utils/supabaseClient'
import './GalleryPage.css'

export default function GalleryPage() {
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    const [lightboxImage, setLightboxImage] = useState(null);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('gallery_events')
                .select(`id, title, event_date, cover_image, is_pinned, gallery_images (image)`)
                .order('is_pinned', { ascending: false })
                .order('event_date', { ascending: false });
            
            if (error) throw error;
            setEvents(data || []);
        } catch (error) {
            console.error("Error fetching gallery events:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEventClick = async (event) => {
        setSelectedEvent(event);
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('gallery_images')
                .select('*')
                .eq('event_id', event.id)
                .order('created_at', { ascending: true });
            
            if (error) throw error;
            setImages(data || []);
        } catch (error) {
            console.error("Error fetching images:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setSelectedEvent(null);
        setImages([]);
    };

    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxImage(images[index].image);
    };

    const closeLightbox = () => {
        setLightboxImage(null);
    };

    const prevImage = (e) => {
        e.stopPropagation();
        const prevIndex = (lightboxIndex - 1 + images.length) % images.length;
        setLightboxIndex(prevIndex);
        setLightboxImage(images[prevIndex].image);
    };

    const nextImage = (e) => {
        e.stopPropagation();
        const nextIndex = (lightboxIndex + 1) % images.length;
        setLightboxIndex(nextIndex);
        setLightboxImage(images[nextIndex].image);
    };

    const isVideo = (url) => {
        if (!url) return false;
        const videoExts = ['.mp4', '.webm', '.ogg', '.mov', '.m4v'];
        return videoExts.some(ext => url.toLowerCase().endsWith(ext));
    };

    const formatDate = (dateString, useLongMonth = false) => {
        if (!dateString) return null;
        const d = new Date(dateString);
        const day = d.getDate();
        const month = d.toLocaleString('en-US', { month: useLongMonth ? 'long' : 'short' });
        const year = d.getFullYear();
        return { day, month, year };
    };

    return (
        <div className="gallery-page-container pb-5">
            <Header title={selectedEvent ? selectedEvent.title : 'Our Gallery'} noMargin />

            <div className="container mt-5">
                {selectedEvent ? (
                    <div className="fade-in-section">
                        <button className="btn btn-outline-primary mb-4 back-btn px-4" onClick={handleBack}>
                            <i className="fa fa-arrow-left me-2"></i> Back to Albums
                        </button>
                        
                        <div className="text-center mb-5">
                            <h2 className="fw-bold display-5 text-primary">{selectedEvent.title}</h2>
                            {selectedEvent.event_date && (() => {
                                const dateObj = formatDate(selectedEvent.event_date, true);
                                return (
                                    <p className="text-muted lead mt-2">
                                        <i className="far fa-calendar-alt me-2 text-primary"></i> 
                                        {dateObj.day} {dateObj.month} {dateObj.year}
                                    </p>
                                );
                            })()}
                        </div>

                        {loading ? (
                            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
                        ) : (
                            <div className="gallery-masonry">
                                {images.map((img, idx) => (
                                    <div className="masonry-item" key={img.id} onClick={() => openLightbox(idx)}>
                                        {isVideo(img.image) ? (
                                            <video src={`${img.image}#t=0.5`} preload="metadata" className="img-fluid" muted loop onMouseOver={e => e.target.play()} onMouseOut={e => e.target.pause()} />
                                        ) : (
                                            <img src={img.image} alt={img.title || 'Gallery image'} className="img-fluid" />
                                        )}
                                        <div className="masonry-overlay">
                                            <i className={isVideo(img.image) ? "fa fa-play-circle" : "fa fa-search-plus"}></i>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {images.length === 0 && !loading && (
                            <div className="text-center py-5">
                                <i className="fa fa-images fa-4x text-muted opacity-25 mb-3"></i>
                                <h4 className="text-muted">No images found for this folder.</h4>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="fade-in-section">
                        <div className="text-center mb-5">
                            <p className="section-title px-5"><span className="px-2">Photo Albums</span></p>
                            <h1 className="mb-4">Explore Our School Memories</h1>
                        </div>

                        {loading ? (
                            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
                        ) : (
                            <div className="row g-4">
                                {events.map(ev => {
                                    const media = ev.gallery_images || [];
                                    const videoCount = media.filter(m => isVideo(m.image)).length;
                                    const photoCount = media.length - videoCount;
                                    
                                    let label = `${photoCount} Photo${photoCount !== 1 ? 's' : ''}`;
                                    if (videoCount > 0) {
                                        label += `, ${videoCount} Video${videoCount !== 1 ? 's' : ''}`;
                                    }

                                    return (
                                        <div className="col-lg-4 col-md-6 mb-4" key={ev.id}>
                                            <div className="album-folder h-100" onClick={() => handleEventClick(ev)}>
                                                <div className="folder-img-wrap">
                                                    <img src={ev.cover_image || 'https://via.placeholder.com/400x300?text=No+Cover'} alt={ev.title} />
                                                    <div className="folder-overlay">
                                                        <div className="d-flex flex-column gap-2 align-items-start">
                                                            {ev.is_pinned && (
                                                                <span className="badge bg-warning text-dark px-2 py-1 rounded" style={{fontSize: '0.7rem', fontWeight: '800'}}>
                                                                    <i className="fa fa-thumbtack me-1"></i> FEATURED
                                                                </span>
                                                            )}
                                                            {ev.event_date && (
                                                                <span className="badge top-left-month">
                                                                    {formatDate(ev.event_date, true).month}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {media.length > 0 && (
                                                            <span className="badge bg-primary photo-count ms-auto">
                                                                <i className={videoCount > 0 ? "fa fa-photo-video" : "fa fa-image"}></i> {label}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="folder-info bg-white p-4">
                                                    <h4 className="mb-2 text-truncate" title={ev.title}>{ev.title}</h4>
                                                    <div className="d-flex justify-content-between text-muted small align-items-center mt-3">
                                                        <span className="d-flex align-items-center" style={{fontSize: '0.9rem'}}>
                                                            <i className="far fa-calendar-alt text-primary me-2"></i> 
                                                            {ev.event_date ? (() => {
                                                                const d = formatDate(ev.event_date);
                                                                return <>{d.day} {d.month} {d.year}</>;
                                                            })() : 'Various Dates'}
                                                        </span>
                                                        <span className="view-link px-3 py-1 rounded-pill bg-light text-primary">View Album &rarr;</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {events.length === 0 && !loading && (
                            <div className="text-center py-5 text-muted">
                                <i className="fa fa-folder-open fa-4x opacity-25 mb-3"></i>
                                <h4>No albums have been created yet.</h4>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightboxImage && (
                <div className="lightbox-backdrop" onClick={closeLightbox}>
                    <button className="lightbox-close" onClick={closeLightbox}>&times;</button>
                    {images.length > 1 && (
                        <>
                            <button className="lightbox-nav lightbox-prev" onClick={prevImage}><i className="fa fa-chevron-left"></i></button>
                            <button className="lightbox-nav lightbox-next" onClick={nextImage}><i className="fa fa-chevron-right"></i></button>
                        </>
                    )}
                    
                    <div className="lightbox-content" onClick={e => e.stopPropagation()}>
                        {isVideo(lightboxImage) ? (
                            <video src={lightboxImage} controls autoPlay className="img-fluid" style={{maxHeight: '85vh', width: 'auto'}} />
                        ) : (
                            <img src={lightboxImage} alt="Fullscreen View" />
                        )}
                        <div className="lightbox-caption text-center mt-3 text-white">
                            {lightboxIndex + 1} / {images.length}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
